/**
 * Admin : rendre un défi public ou privé, choisir son fond SKILLCARD (défi sans
 * photo ni vidéo), ou le supprimer (cf. `challenge-admin.ts`).
 */
import { useState } from 'react';
import { Check, Eye, EyeOff, Trash2, X } from 'lucide-react';
import {
  useDeleteChallenge,
  useModerateChallenge,
  useSetChallengeBackground,
  useSetChallengePublic,
} from './challenge-admin';
import { ChallengeBackgroundPicker } from './challenge-background-picker';

export interface ChallengeManageCardProps {
  challenge: {
    id: string;
    title: string;
    exercise_id: string;
    media_type: 'none' | 'photo' | 'video';
    background_card_id: string | null;
    is_public: boolean;
    is_flagged: boolean;
    public_request?: 'none' | 'pending' | 'approved' | 'rejected';
    media_url: string | null;
    thumbnail_url: string | null;
  };
  /** appelé après une suppression réussie */
  onDeleted: () => void;
}

export function ChallengeManageCard({ challenge, onDeleted }: ChallengeManageCardProps) {
  const setPublic = useSetChallengePublic();
  const del = useDeleteChallenge();
  const setBackground = useSetChallengeBackground();
  const moderate = useModerateChallenge();
  const [error, setError] = useState<string | null>(null);
  const isPublic = challenge.is_public;

  const toggle = () => {
    setError(null);
    setPublic.mutate(
      { id: challenge.id, isPublic: !isPublic },
      { onError: (e) => setError(e.message) },
    );
  };

  const remove = () => {
    if (
      !window.confirm(
        `Supprimer le défi « ${challenge.title} » ? Son média et ses réponses seront effacés. C’est définitif.`,
      )
    )
      return;
    setError(null);
    del.mutate(
      { id: challenge.id, mediaUrl: challenge.media_url, thumbnailUrl: challenge.thumbnail_url },
      { onSuccess: onDeleted, onError: (e) => setError(e.message) },
    );
  };

  return (
    <section className="border-ink-100 mb-4 rounded-2xl border bg-white p-4">
      <h2 className="font-display text-ink-900 text-base font-bold">Visibilité</h2>
      {challenge.public_request === 'pending' && (
        <div
          className="bg-accent-50 ocean:bg-accent-400/10 mt-2 rounded-xl p-3"
          data-testid="challenge-review"
        >
          <p className="text-ink-900 font-sans text-sm font-semibold">
            L’auteur demande la page d’accueil
          </p>
          <p className="text-ink-600 font-sans text-xs">
            Vérifie la vidéo ou la photo et le titre avant de publier.
          </p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              disabled={moderate.isPending}
              onClick={() => {
                setError(null);
                moderate.mutate(
                  { id: challenge.id, approve: true },
                  { onError: (e) => setError(e.message) },
                );
              }}
              className="bg-primary-500 inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl px-4 font-sans text-sm font-bold text-white active:scale-[0.98] disabled:opacity-50"
            >
              <Check className="h-4 w-4" /> Approuver et publier
            </button>
            <button
              type="button"
              disabled={moderate.isPending}
              onClick={() => {
                setError(null);
                moderate.mutate(
                  { id: challenge.id, approve: false },
                  { onError: (e) => setError(e.message) },
                );
              }}
              className="border-ink-200 text-ink-800 inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl border px-4 font-sans text-sm font-bold active:scale-[0.98] disabled:opacity-50"
            >
              <X className="h-4 w-4" /> Refuser
            </button>
          </div>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-ink-900 flex items-center gap-1.5 font-sans text-sm font-semibold">
            {isPublic ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {isPublic ? 'Public' : 'Privé'}
          </p>
          <p className="text-ink-500 font-sans text-xs">
            {isPublic
              ? 'Visible dans le bloc des défis de la landing et par son lien.'
              : 'Pas sur la landing ; sa page s’ouvre seulement par son lien (non référencée).'}
            {challenge.public_request === 'rejected' && ' Demande de l’auteur refusée.'}
            {challenge.is_flagged && ' Signalé : jamais affiché tant qu’il l’est.'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isPublic}
          aria-label="Défi public"
          disabled={setPublic.isPending || del.isPending}
          onClick={toggle}
          className="relative inline-flex min-h-[2.75rem] min-w-[3.5rem] shrink-0 items-center justify-center transition active:scale-[0.98] disabled:opacity-50"
        >
          <span
            className={`relative h-7 w-12 rounded-full transition-colors ${isPublic ? 'bg-primary-500' : 'bg-ink-200'}`}
          >
            <span
              className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${isPublic ? 'left-[1.375rem]' : 'left-0.5'}`}
            />
          </span>
        </button>
      </div>
      <p className="text-ink-400 mt-2 font-sans text-xs">La landing se met à jour dans l’heure.</p>

      {challenge.media_type === 'none' && (
        <div className="mt-4">
          <ChallengeBackgroundPicker
            exerciseId={challenge.exercise_id}
            value={challenge.background_card_id}
            disabled={setBackground.isPending}
            onChange={(cardId) => {
              setError(null);
              setBackground.mutate(
                { id: challenge.id, cardId },
                { onError: (e) => setError(e.message) },
              );
            }}
          />
        </div>
      )}

      <button
        type="button"
        onClick={remove}
        disabled={del.isPending}
        className="mt-4 inline-flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border border-red-200 px-4 font-sans text-sm font-bold text-red-600 transition hover:bg-red-50 active:scale-[0.98] disabled:opacity-50 sm:w-auto"
      >
        <Trash2 className="h-4 w-4" />
        {del.isPending ? 'Suppression…' : 'Supprimer ce défi'}
      </button>

      {error && (
        <p role="alert" className="mt-2 font-sans text-xs text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}
